<script>
import BaseButton from '../_base-button.vue'
import BaseIcon from '../_base-icon.vue'

export default {
    name: 'AuthPromptModal',

    components: {
        BaseButton,
        BaseIcon,
    },

    props: {
        data: {
            type: Object,
            default: () => ({}),
        },
    },

    computed: {
        headline() {
            return this.data.headline
        },
        description() {
            return this.data.description
        },
        primaryAction() {
            return this.data.primaryAction
        },
        secondaryAction() {
            return this.data.secondaryAction
        },
    },

    methods: {
        close() {
            this.$emit('close')
        },
        handlePrimaryAction() {
            if (this.primaryAction.route) {
                this.$router.push(this.primaryAction.route)
            }
            if (this.primaryAction.callback) {
                this.primaryAction.callback()
            }
            this.close()
        },
        handleSecondaryAction() {
            if (!this.secondaryAction) return

            if (this.secondaryAction.route) {
                this.$router.push(this.secondaryAction.route)
            }
            if (this.secondaryAction.callback) {
                this.secondaryAction.callback()
            }
            this.close()
        },
    },
}
</script>

<template>
    <div :class="$style.modal">
        <button
            :class="$style.closeButton"
            aria-label="Close modal"
            @click="close"
        >
            <BaseIcon
                :class="$style.closeIcon"
                name="close-outline"
            />
        </button>

        <div :class="$style.content">
            <h2 :class="$style.headline">{{ headline }}</h2>
            <p
                v-if="description"
                :class="$style.description"
            >
                {{ description }}
            </p>

            <div :class="$style.actions">
                <BaseButton
                    :class="$style.primaryButton"
                    size="large"
                    button-type="primaryFlat"
                    @click="handlePrimaryAction"
                >
                    {{ primaryAction.text }}
                </BaseButton>

                <button
                    v-if="secondaryAction"
                    :class="$style.secondaryLink"
                    @click="handleSecondaryAction"
                >
                    {{ secondaryAction.text }}
                </button>
            </div>
        </div>
    </div>
</template>

<style module lang="scss">
@use '@design' as *;

.modal {
    position: relative;
    width: 90vw;
    max-width: 710px;
    padding: $s-h 64px 48px;
    background: #27292D;
    border: none;
    border-radius: 12px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.95);

    @include mobileOnly {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: calc(100vw - $s-xxl);
        max-width: none;
        height: auto;
        padding: $s-xxl 30px 36px;
        margin: $s-l;
        border-radius: $s-m;
    }
}

.closeButton {
    position: absolute;
    top: $s-l;
    right: $s-l;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: $s-xxs;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 50%;
    transition: all 0.2s ease;

    .closeIcon {
        width: $s-l;
        height: $s-l;
    }

    &:hover {
        color: rgba(255, 255, 255, 0.8);
        background: rgba(255, 255, 255, 0.08);
    }

    @include mobileOnly {
        top: $s-l;
        right: $s-l;
    }
}

.content {
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    text-align: left;
}

.headline {
    margin: 0;
    margin-bottom: 140px;
    font-family: aktiv-grotesk-extended, -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
    font-size: 35px;
    font-weight: 700;
    line-height: 1.2;
    color: $c-white;
    text-align: left;
    letter-spacing: -0.03em;
    white-space: pre-wrap;

    @include mobileOnly {
        max-width: 280px;
        margin-top: $s-xl;
        margin-bottom: 80px;
        font-size: 27px;
        line-height: 1.15;
    }
}

.description {
    @extend %p;

    margin: -120px 0 120px 0;
    font-size: 16px;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.7);
    text-align: left;

    @include mobileOnly {
        margin: -60px 0 60px 0;
        font-size: 14px;
    }
}

.actions {
    display: flex;
    flex-direction: column;
    gap: 1px;
    align-items: center;
    width: 100%;
    margin-top: $s-xs;

    @include mobileOnly {
        flex-direction: row;
        flex-wrap: wrap;
        gap: $s-m;
        align-items: center;
        justify-content: center;
        margin-top: $s-xl;
    }
}

.primaryButton {
    width: 100%;
    max-width: 400px;
    padding: $s-l 36px;
    font-size: $s-m;
    text-transform: none;
    letter-spacing: normal;
    border-radius: 50px;

    @include mobileOnly {
        flex: 0 0 auto;
        width: auto;
        padding: $s-m 28px;
    }
}

.secondaryLink {
    @extend %p;

    align-self: center;
    font-size: 15px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
    text-align: center;
    text-decoration: none;
    letter-spacing: 0.01em;
    cursor: pointer;
    background: transparent;
    border: none;
    transition: all 0.2s ease;

    @include mobileOnly {
        flex-shrink: 0;
        align-self: center;
        padding: 0 $s-xs;
        margin-top: 0;
        font-size: 14px;
        white-space: nowrap;
    }

    &:hover {
        font-weight: 700;
        color: rgba(255, 255, 255, 0.95);
    }
}
</style>